/**
 * basemaps.js — Fuente única de verdad para los basemaps (tiles) de todos los
 * mapas Leaflet de la aplicación.
 *
 * Antes cada mapa hardcodeaba su URL de tiles (CARTO/OSM) y la lógica
 * claro/oscuro. Ahora todos consumen este módulo: la elección se persiste en
 * localStorage y se propaga en caliente a TODOS los mapas montados mediante el
 * evento global 'basemapchange' (ver useBasemapTile).
 *
 * Cada basemap define una variante `light` y una `dark`, cada una con:
 *   { url, attribution, subdomains?, maxZoom }
 */
import { useEffect, useState } from 'react';

const OSM_CARTO_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';
const ESRI_ATTRIBUTION = 'Tiles &copy; Esri';

const STADIA_KEY = process.env.REACT_APP_STADIA_KEY;

// CARTO exige API key en sus basemaps raster desde agosto de 2026: sin ella el
// CDN NO devuelve un error HTTP — responde 200 con un tile marcado
// "API KEY REQUIRED", así que la degradación es silenciosa y solo se ve en
// pantalla. La key se inyecta por entorno (nunca se versiona) y si falta, los
// basemaps CARTO directamente no se ofrecen: mejor un basemap sin marca que un
// mapa con la marca de agua encima.
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY;
const CARTO_KEY_PARAM = CARTO_KEY ? `?key=${encodeURIComponent(CARTO_KEY)}` : '';

/**
 * Query string con la API key de CARTO ('' si no hay key configurada). Lo usan
 * los consumidores que arman URLs de tiles CARTO a mano y no pasan por el
 * catálogo de abajo (p. ej. el mapa estático del PDF de entrega).
 */
export const CARTO_KEY_QUERY = CARTO_KEY_PARAM;

/** ¿Hay API key de CARTO configurada en este entorno? */
export const hasCartoKey = Boolean(CARTO_KEY);

/**
 * URL de tiles raster de CARTO en formato Leaflet, con la API key del entorno.
 * @param {string} style ruta del estilo, p. ej. 'light_all' o 'rastertiles/voyager'
 * @returns {string}
 */
function cartoUrl(style) {
  return `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png${CARTO_KEY_PARAM}`;
}

// Basemaps de CARTO: solo se publican si hay API key (ver CARTO_KEY arriba).
const CARTO_BASEMAPS = {
  'carto-positron': {
    label: 'CARTO Positron',
    light: {
      url: cartoUrl('light_all'),
      attribution: OSM_CARTO_ATTRIBUTION,
      subdomains: 'abcd',
      maxZoom: 20,
    },
    dark: {
      url: cartoUrl('dark_all'),
      attribution: OSM_CARTO_ATTRIBUTION,
      subdomains: 'abcd',
      maxZoom: 20,
    },
  },
  'carto-voyager': {
    label: 'CARTO Voyager',
    light: {
      url: cartoUrl('rastertiles/voyager'),
      attribution: OSM_CARTO_ATTRIBUTION,
      subdomains: 'abcd',
      maxZoom: 20,
    },
    dark: {
      url: cartoUrl('dark_all'),
      attribution: OSM_CARTO_ATTRIBUTION,
      subdomains: 'abcd',
      maxZoom: 20,
    },
  },
};

// ── Catálogo de basemaps ──────────────────────────────────────────────────
export const BASEMAPS = {
  ...(CARTO_KEY ? CARTO_BASEMAPS : {}),
  // Esri Gray Canvas no requiere key: es el basemap de respaldo cuando CARTO no
  // está disponible, y por eso siempre está en el catálogo.
  'esri-gray': {
    label: 'Esri Gray Canvas',
    light: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      attribution: ESRI_ATTRIBUTION,
      maxZoom: 16,
    },
    dark: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      attribution: ESRI_ATTRIBUTION,
      maxZoom: 16,
    },
  },
};

// Stadia Alidade Smooth — solo disponible si hay API key configurada.
if (STADIA_KEY) {
  BASEMAPS['stadia-smooth'] = {
    label: 'Stadia Alidade Smooth',
    light: {
      url: `https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${STADIA_KEY}`,
      attribution:
        '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 20,
    },
    dark: {
      url: `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${STADIA_KEY}`,
      attribution:
        '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 20,
    },
  };
}

// Basemap por defecto (configurable por env). Cae al de respaldo si la env
// apunta a un id inexistente o a un CARTO sin key en este entorno.
const FALLBACK_BASEMAP = CARTO_KEY ? 'carto-positron' : 'esri-gray';

export const DEFAULT_BASEMAP =
  (process.env.REACT_APP_MAP_BASEMAP && BASEMAPS[process.env.REACT_APP_MAP_BASEMAP]
    ? process.env.REACT_APP_MAP_BASEMAP
    : null) || FALLBACK_BASEMAP;

const STORAGE_KEY = 'mapBasemap';
const BASEMAP_EVENT = 'basemapchange';

/**
 * Devuelve el id del basemap activo: el guardado en localStorage si es válido,
 * si no el DEFAULT_BASEMAP.
 */
export function getActiveBasemapId() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && BASEMAPS[stored]) return stored;
  } catch {}
  return DEFAULT_BASEMAP;
}

/**
 * Persiste el basemap elegido y notifica a todos los mapas montados para que
 * re-rendericen sus tiles (vía el hook useBasemapTile).
 */
export function setActiveBasemap(id) {
  if (!BASEMAPS[id]) return;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {}
  window.dispatchEvent(new Event(BASEMAP_EVENT));
}

/**
 * Devuelve la configuración de tile { url, attribution, subdomains, maxZoom }
 * de la variante (clara/oscura) del basemap activo.
 * @param {boolean} isDark
 */
export function getTile(isDark) {
  const basemap = BASEMAPS[getActiveBasemapId()] || BASEMAPS[DEFAULT_BASEMAP];
  return isDark ? basemap.dark : basemap.light;
}

/**
 * Hook reactivo: devuelve la tile activa para la variante clara/oscura y se
 * re-renderiza cuando cambia el basemap (evento 'basemapchange'). Así, cambiar
 * el basemap desde Configuración actualiza en caliente TODOS los mapas montados.
 * @param {boolean} isDark
 */
export function useBasemapTile(isDark) {
  const [tile, setTile] = useState(() => getTile(isDark));

  useEffect(() => {
    const update = () => setTile(getTile(isDark));
    update(); // re-sincroniza al cambiar isDark
    window.addEventListener(BASEMAP_EVENT, update);
    return () => window.removeEventListener(BASEMAP_EVENT, update);
  }, [isDark]);

  return tile;
}
